import { TagType } from 'types/types'
import { computed, Ref } from 'vue'
import { useLogger, VueLogger } from 'vue-logger-plugin'

import store from '@/store'
import { AnnotatedSentence } from '@/util/AnnotatedSentence'
import { SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'
import { hasMelody, inReadMode, VoiceStyleSet } from '@/util/VoiceStyles'

type StyledRecordings = Record<string, Array<SentenceRecordingMetadataWithPitchedAudios>>

//
// Build a map from sentenceId to all the recordings that match the current
// annotatedSentences and voiceStyleset criteria.
//

const computeRecordingsInStyle = (payload: {
  voiceStyleSet: VoiceStyleSet
  annotatedSentences: Array<AnnotatedSentence>
  logger: VueLogger
}): StyledRecordings => {
  const log = (...args: unknown[]) => {
    if (payload.logger) payload.logger.debug(...(args as [string, ...unknown[]]))
  }

  const tags = [payload.voiceStyleSet.tags].flat() as Array<TagType>

  const recordingsInStyle = {} as StyledRecordings

  if (!store.getters.audio.hasRecordings || !payload.annotatedSentences.length) {
    log('empty audio.recordings')
    return recordingsInStyle
  }

  payload.annotatedSentences.forEach((annotatedSentence: AnnotatedSentence) => {
    const sentenceId = annotatedSentence.sentence().id
    const hasRecording = store.getters.audio.hasRecordingsForSentence(sentenceId)
    log(`sentenceId ${sentenceId} hasRecording ${hasRecording}`)

    // figure out whether this recording matches the filtering requirements:
    // pronunciation, melodic style
    if (!hasRecording) {
      log(`sentenceId ${sentenceId} has no recording, returning`)
      return
    }

    // go through each recording for this sentence and see if the styles
    // match.
    store.getters.audio
      .getRecordingListForSentence(sentenceId)
      .forEach((sentenceRecordingMetadataWithPitchedAudios: SentenceRecordingMetadataWithPitchedAudios) => {
        const { haftarahMelody, id, pronunciation, sentenceGroupMelody, torahMelody } =
          sentenceRecordingMetadataWithPitchedAudios.sentenceRecording!

        // pronunciation style is not correct
        if (payload.voiceStyleSet.pronunciation!.id !== pronunciation!.id) {
          log(`recording ${id} for sentenceId ${sentenceId} wrong pronunciation`)
          return
        }

        // we're looking for read recordings, but this one has a melodic style
        // set; skip
        if (inReadMode(payload.voiceStyleSet)) {
          if (hasMelody(sentenceRecordingMetadataWithPitchedAudios.sentenceRecording!)) {
            log(`recording ${id} for sentenceId ${sentenceId} in read mode, but has melody`)
            return
          }

          // yes, it's sung, but torah melodic style does not match
        } else if (payload.voiceStyleSet.torahMelody && (!torahMelody || torahMelody.id !== payload.voiceStyleSet.torahMelody.id)) {
          log(`recording ${id} for sentenceId ${sentenceId} bad torah melody style match`)
          return

          // yes, it's sung, but haftarah melodic style does not match
        } else if (
          payload.voiceStyleSet.haftarahMelody &&
          (!haftarahMelody || haftarahMelody.id !== payload.voiceStyleSet.haftarahMelody.id)
        ) {
          log(`recording ${id} for sentenceId ${sentenceId} bad haftarah melody style match`)
          return

          // yes, it's sung, but sentenceGroup melodic style does not match
        } else if (
          payload.voiceStyleSet.sentenceGroupMelody &&
          (!sentenceGroupMelody || sentenceGroupMelody.id !== payload.voiceStyleSet.sentenceGroupMelody.id)
        ) {
          log(`recording ${id} for sentenceId ${sentenceId} bad sentence_group melody style match`)
          return
        }

        // if the current tag selection is empty, then match only those recordings
        // that have no tags.
        //
        // if the current tag selection is not empty, then match only those
        // recordings that include all selected tags; note that this may match
        // multiple recordings; for example if tag selection is ["A"], then a
        // recording with ["A"] or ["A", "B"] both match.
        log(`recording ${id} for sentenceId ${sentenceId}, tags = `)
        log(sentenceRecordingMetadataWithPitchedAudios.sentenceRecording!.tags)
        log(`voiceStyleSet.tags = `)
        log(tags)

        if (!tags.length) {
          if (sentenceRecordingMetadataWithPitchedAudios.sentenceRecording!.tags.length) {
            log(`recording ${id} for sentenceId ${sentenceId} empty tag selection, but recording has tags`)
            return
          }
        } else {
          const tagsMatch = tags.every((tagX: TagType) => {
            const r = sentenceRecordingMetadataWithPitchedAudios.sentenceRecording!.tags.find((tagY) => tagX.id === tagY.id)
            log(`recording ${id} for sentenceId ${sentenceId} matching voiceStyleSet.tags ${tagX.name}, matched =`)
            log(r)
            return !!r
          })
          log(`recording ${id} for sentenceId ${sentenceId} matching voiceStyleSet.tags, tagsMatch = ${tagsMatch}`)

          if (!tagsMatch) {
            log(`recording ${id} for sentenceId ${sentenceId} bad tag match`)
            return
          }
        }

        // all criteria match! prepare the array to hold a value
        log(`recording ${id} for sentenceId ${sentenceId} tags match!`)
        if (!recordingsInStyle[sentenceId]?.length) {
          recordingsInStyle[sentenceId] = [] as Array<SentenceRecordingMetadataWithPitchedAudios>
        }

        // This sentenceRecordingMetadataWithPitchedAudios matches all the criteria; collect it.
        // Multiple recordings may match (e.g. tag supersets); the exact-match tiebreaker below
        // will narrow down to the best one if possible.
        recordingsInStyle[sentenceId].push(sentenceRecordingMetadataWithPitchedAudios)
      })
  })

  // We now have an object that matches sentenceIds to an array of
  // SentenceRecordingMetadataWithPitchedAudios. The only reason for there being more than one
  // match is if multiple recordings matched the tag selection. There is one
  // special case we want to deal with here: if there is an *exact* match
  // between the tag selection and the tags on the recording (and there's
  // only one of those exact matches), we want to return just that recording,
  // and not any other recording.
  const sortedVoiceStyleTags = [...tags].sort()
  Object.keys(recordingsInStyle).forEach((sentenceId) => {
    const exactRecordingMatches = (recordingsInStyle[sentenceId] as Array<SentenceRecordingMetadataWithPitchedAudios>).filter(
      (sentenceRecordingMetadataWithPitchedAudios: SentenceRecordingMetadataWithPitchedAudios) => {
        if (!sentenceRecordingMetadataWithPitchedAudios.sentenceRecording) {
          payload.logger && payload.logger.error('unexpected null sentenceRecordingMetadataWithPitchedAudios.sentenceRecording')
          return false
        }
        const sortedSentenceRecordingTags = [...sentenceRecordingMetadataWithPitchedAudios.sentenceRecording.tags].sort()
        return JSON.stringify(sortedVoiceStyleTags) === JSON.stringify(sortedSentenceRecordingTags)
      },
    )
    if (exactRecordingMatches.length === 1) {
      recordingsInStyle[sentenceId] = [exactRecordingMatches[0]] as Array<SentenceRecordingMetadataWithPitchedAudios>
    }
  })

  log('recordingsInStyle is done, recordingsInStyle =')
  log(recordingsInStyle)
  return recordingsInStyle
}

const useStyledRecordings = (annotatedSentences: Ref<Array<AnnotatedSentence>>, voiceStyleSet: Ref<VoiceStyleSet>) => {
  const logger = useLogger()

  function prepareStyledRecordings(): void {
    store.dispatch.user.fetchUser()
    store.dispatch.style.fetchStyles()
  }

  const recordingsInStyle = computed<StyledRecordings>((): StyledRecordings => {
    logger.debug('recordingsInStyle')
    return computeRecordingsInStyle({
      annotatedSentences: annotatedSentences.value as Array<AnnotatedSentence>,
      logger,
      voiceStyleSet: voiceStyleSet.value,
    })
  })

  return {
    prepareStyledRecordings,
    recordingsInStyle,
  }
}

export { computeRecordingsInStyle, StyledRecordings, useStyledRecordings }
